import { IconEnvState } from "@/assets/icons/geist/IconEnvState";
import {
    StepAutomations,
    StepClone,
    StepStart,
    StepStartDevcontainer,
} from "@/components/environments/EnvironmentStartSteps";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { SkeletonBlock, SkeletonText } from "@/components/podkit/loading/Skeleton";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/podkit/select/Select";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading1 } from "@/components/podkit/typography/Headings";
import { duration } from "@/format/time";
import { useEnvironmentComputeDescription } from "@/hooks/use-environment-compute-description";
import { useEnvironment, useUpdateEnvironment, type PlainEnvironment } from "@/queries/environment-queries";
import { useRunner } from "@/queries/runner-queries";
import { EnvironmentActionsDropdown } from "@/routes/environments/details/EnvironmentActionsDropdown";
import { effectiveState, type EffectiveState } from "@/routes/environments/phase";
import { formatError } from "@/utils/errors";
import type { Duration, PlainMessage } from "@bufbuild/protobuf";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { useCallback, useMemo, useState, type FC, type PropsWithChildren, type ReactNode } from "react";

export const EnvironmentStartSqeuence: FC<{ environmentId: string }> = ({ environmentId }) => {
    const { data: environment } = useEnvironment(environmentId);
    const { data: runner } = useRunner(environment?.metadata?.runnerId);

    const state = useMemo(() => {
        if (environment && runner) {
            return effectiveState(environment, runner);
        }
    }, [environment, runner]);

    if (!runner || !environment) {
        return <EnvironmentStartSequenceLoading />;
    }

    return <FoldableSequence environment={environment} state={state} />;
};
const FoldableSequence: FC<{ state?: EffectiveState; environment: PlainEnvironment }> = ({ environment, state }) => {
    const [expanded, setExpanded] = useState(true);

    const toggleExpanded = useCallback(() => {
        setExpanded((current) => !current);
    }, []);

    return (
        <div className={cn("relative", expanded ? "hover:rounded-xl hover:shadow-lg" : "")}>
            <div
                className={cn(
                    "relative flex h-16 cursor-pointer border border-border-base bg-[rgb(var(--surface-glass))]",
                    expanded ? "rounded-t-xl" : "z-[5] mb-4 rounded-xl hover:shadow-lg",
                )}
                onClick={toggleExpanded}
            >
                <Header environment={environment} state={state} />
            </div>

            <div
                className={cn(
                    "max-h-screen transition-all duration-300 ease-linear fade-in",
                    expanded ? "" : "absolute top-0 w-full",
                )}
            >
                <Foldable expanded={expanded} className={expanded ? "" : "z-[4]"}>
                    <StepStart environment={environment} />
                </Foldable>
                <Foldable expanded={expanded} className={expanded ? "" : "z-[3]"}>
                    <StepClone environment={environment} />
                </Foldable>
                <Foldable expanded={expanded} className={expanded ? "" : "z-[2]"}>
                    <StepAutomations environment={environment} />
                </Foldable>
                <Foldable expanded={expanded} className={expanded ? "" : "z-[1]"}>
                    <StepStartDevcontainer environment={environment} />
                </Foldable>
            </div>
        </div>
    );
};

const Foldable: FC<{ expanded: boolean } & PropsWithChildren & PropsWithClassName> = ({
    expanded,
    children,
    className,
}) => {
    return (
        <div
            className={cn(
                "relative flex w-full border border-border-base bg-surface-glass transition-all delay-0 duration-200 ease-linear fade-in first:pt-2",
                expanded
                    ? "mt-0 border-b-0 border-t-0 last:rounded-b-xl last:border-b"
                    : "relative -mt-[61px] h-16 rounded-xl first:mt-[3px]",
                className,
            )}
        >
            <div className={expanded ? "w-full opacity-100" : "w-full opacity-0 transition-all fade-out"}>
                {children}
            </div>
        </div>
    );
};

const EnvironmentStartSequenceLoading: FC = () => {
    return (
        <Container>
            <div className="border-border flex w-full items-center gap-2 rounded-t-xl bg-surface-glass px-4 py-3">
                <div className="flex size-6 items-center justify-center">
                    <SkeletonBlock className="size-5" ready={false} />
                </div>
                <SkeletonText size="lg" ready={false} className="w-20" />
            </div>
            <div className="flex flex-col border-t border-border-base">
                {["w-20", "w-80", "w-72"].map((w, i) => (
                    <div key={i} className="flex h-10 w-full items-center gap-2 px-4 py-2">
                        <div className="flex size-6 items-center justify-center">
                            <SkeletonBlock className="size-5" ready={false} />
                        </div>
                        <SkeletonText size="lg" ready={false} className={w} />
                    </div>
                ))}
            </div>
        </Container>
    );
};

const Container: FC<PropsWithChildren<{ state?: EffectiveState }>> = ({ children }) => {
    return <div className="flex w-full flex-col rounded-xl border border-border-base bg-surface-glass">{children}</div>;
};

const Header: FC<{
    state?: EffectiveState;
    environment: PlainEnvironment;
    onClick?: () => void;
}> = ({ state, environment, onClick }) => {
    const description = useEnvironmentComputeDescription(environment);
    const details = [description?.runnerName, description?.clazz].filter(Boolean).join("\u00A0\u00A0•\u00A0\u00A0");

    return (
        <button className={cn("flex h-16 w-full gap-2 rounded-t-xl px-4 py-3")} onClick={onClick}>
            <div className="flex h-full grow flex-row items-center gap-2 truncate">
                <StatusIndicator state={state} />
                <StatusText state={state} />
                <span className="-mb-0.5 truncate text-base text-content-secondary">{details}</span>
            </div>
            <AutoStop key={`${environment.id}-auto-stop`} environment={environment} />
            <EnvironmentActionsDropdown environment={environment} />
        </button>
    );
};

const AutoStop: FC<{ environment: PlainEnvironment } & PropsWithClassName> = ({ className, environment }) => {
    const { toast } = useToast();
    const [open, setOpen] = useState(false);
    const timeoutValues = computeTimeoutSelect(environment);
    const [value, setValue] = useState<{ label: string; value: string } | undefined>(initialTimeoutValue(environment));

    const updateEnvironment = useUpdateEnvironment();
    const handleOnValueChanged = useCallback(
        async (newValue: string) => {
            const original = value;
            try {
                const seconds = BigInt(newValue);
                setValue(toTimeout(Number(newValue)));
                await updateEnvironment.mutateAsync({
                    req: {
                        environmentId: environment.id,
                        spec: { timeout: { disconnected: { seconds } } },
                    },
                });
                toast({
                    title: "Successfully updated auto-stop",
                });
            } catch (error) {
                setValue(original);
                toast({
                    title: "Failed to update auto-stop value",
                    description: formatError(error),
                });
            }
        },
        [value, updateEnvironment, environment.id, toast],
    );

    if (environment.status?.phase !== EnvironmentPhase.RUNNING) {
        return null;
    }

    return (
        <div className={cn("flex items-center gap-2", className)}>
            <div
                className="inline-block text-nowrap py-2 text-base text-content-secondary"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpen((o) => !o);
                }}
            >
                Auto-stop after {"\u00A0•"}
            </div>
            <Select value={value?.value || ""} onValueChange={handleOnValueChanged} onOpenChange={setOpen} open={open}>
                <SelectTrigger loading={false} className="border-0 bg-transparent p-0 ring-0 focus-visible:ring-0">
                    <span className="inline-block truncate text-content-secondary">
                        <SelectValue className="text-content-secondary" placeholder={"Select a timeout"}>
                            {value?.label || ""}
                        </SelectValue>
                    </span>
                    <SelectContent>
                        <SelectGroup>
                            {timeoutValues.map((t) => (
                                <SelectItem key={t.value} value={t.value}>
                                    <div className="flex w-full flex-row items-center justify-between pr-10">
                                        <div>{t.label}</div>
                                    </div>
                                </SelectItem>
                            ))}
                        </SelectGroup>
                    </SelectContent>
                </SelectTrigger>
            </Select>
        </div>
    );
};

function initialTimeoutValue(environment: PlainEnvironment) {
    const timeout = toTimeoutValue(environment?.spec?.timeout?.disconnected);
    return toTimeout(timeout);
}
function computeTimeoutSelect(environment: PlainEnvironment): { label: string; value: string }[] {
    const timeout = toTimeoutValue(environment?.spec?.timeout?.disconnected);
    const timeouts: number[] = [30 * 60, 60 * 60, 60 * 60 * 3, 60 * 60 * 8];
    const customTimeout = timeout !== 0 && timeouts.find((seconds) => seconds === timeout) === undefined;
    if (customTimeout) {
        timeouts.push(timeout);
        timeouts.sort((a, b) => a - b);
    }
    timeouts.push(0);
    return timeouts.map(toTimeout);
}

function toTimeout(seconds: number): { label: string; value: string } {
    if (seconds <= 0) {
        return {
            label: "Never",
            value: "0",
        };
    }
    return {
        label: duration(seconds) + " of inactivity",
        value: String(seconds),
    };
}

function toTimeoutValue(duration?: PlainMessage<Duration>): number {
    return duration === undefined ? 30 * 60 : Number(duration.seconds);
}

const StatusText: FC<{ state?: EffectiveState }> = ({ state }) => {
    if (!state) {
        return null;
    }

    let text = "Unknown";

    switch (state.state) {
        case EnvironmentPhase.RUNNING:
            text = "Running";
            break;
        case EnvironmentPhase.CREATING:
        case EnvironmentPhase.STARTING:
            text = "Starting";
            break;
        case EnvironmentPhase.UPDATING:
            text = "Updating";
            break;
        case EnvironmentPhase.STOPPING:
            text = "Stopping";
            if (state.timeout) {
                text = `Auto-stopping`;
            }
            break;
        case EnvironmentPhase.STOPPED:
            text = "Stopped";
            if (state.timeout) {
                text = `Auto-stopped`;
            }
            break;
        case EnvironmentPhase.DELETING:
            text = "Deleting";
            break;
    }

    if (state.failures && state.state !== EnvironmentPhase.DELETING) {
        text = "Failed";
    }

    if (state.state === "OFFLINE") {
        text = "Runner is offline";
    }

    return <Heading1 className="text-xl">{text}</Heading1>;
};

const StatusIndicator: FC<{
    state?: EffectiveState;
}> = ({ state }) => {
    if (!state) {
        return null;
    }

    let icon: ReactNode;
    let animate = false;
    switch (state.state) {
        case EnvironmentPhase.RUNNING:
            icon = <IconEnvState state="success" />;
            break;
        case EnvironmentPhase.CREATING:
        case EnvironmentPhase.STARTING:
        case EnvironmentPhase.UPDATING:
        case EnvironmentPhase.DELETING:
        case EnvironmentPhase.STOPPING:
            icon = <IconEnvState state="inprogress2" />;
            animate = true;
            break;
        case EnvironmentPhase.STOPPED:
            icon = <IconEnvState state="waiting" />;
            break;
    }

    if (state.failures && state.state !== EnvironmentPhase.DELETING) {
        icon = <IconEnvState state="waiting" />;
    }

    if (state.state === "OFFLINE") {
        icon = <IconEnvState state="waiting" />;
    }

    return <div className={cn("flex size-6 items-center justify-center", animate ? "animate-pulse" : "")}>{icon}</div>;
};
